import React from "react";
import firebase from "firebase/compat/app";
import { useNavigate } from "react-router-dom";
import {
    FiArrowRight,
    FiEye,
    FiMessageCircle,
    FiSearch,
} from "react-icons/fi";
import {
    aboutContent,
    blogContent,
} from "../../data/portfolioContent";
import { firestore } from "../../configs/firebase";
import {
    estimateReadTime,
    formatDate,
    getBlogCategories,
    hasHtmlContent,
    resolveBlogText,
    sanitizeRichHtml,
    stripHtml,
} from "../../utils/blogAdmin";
import { pick } from "../../utils/localization";
import PageFooter from "./PageFooter";
import BlogEngagement from "./BlogEngagement";
import CryptoTicker from "../CryptoTicker";

const BLOG_COLLECTION = "blogs";
const PAGE_SIZE = 5;

const blogCopy = {
    en: {
        all: "All",
        allDates: "All dates",
        allHashtags: "All hashtags",
        back: "< Back to blogs",
        breadcrumbBlog: "Blog",
        breadcrumbHome: "Home",
        empty: "No blog posts match these filters.",
        filterTitle: "Topics",
        hashtagLabel: "Filter by hashtag",
        loadMore: "Load more posts",
        loading: "Loading blog posts...",
        notFound: "This blog post was not found or is not published yet.",
        openPost: "Open blog post",
        readPost: "Read post",
        reads: (views) => `${views} reads`,
        resultCount: (count) => `${count} ${count === 1 ? "post" : "posts"} found`,
        searchLabel: "Search blogs",
        searchPlaceholder: "Search blog title, content, category, hashtag...",
        selectDateLabel: "Time",
        sortLabel: "Sort blogs",
        sortOptions: [
            { id: "newest", label: "Newest -> Oldest" },
            { id: "oldest", label: "Oldest -> Newest" },
            { id: "popular", label: "Popular / Most read" },
        ],
        toc: "Contents",
    },
    vi: {
        all: "Tất cả",
        allDates: "Tất cả ngay",
        allHashtags: "Tất cả hashtag",
        back: "< Quay lại blog",
        empty: "Không có bài viết phù hợp với bộ lọc.",
        hashtagLabel: "Lọc theo hashtag",
        loadMore: "Tải thêm bài viết",
        loading: "Đang tải bài viết...",
        notFound: "Không tìm thấy bài viết hoặc bài viết chưa được published.",
        openPost: "Mở bài viết",
        reads: (views) => `${views} lướt đọc`,
        resultCount: (count) => `${count} bài viết phù hợp`,
        searchLabel: "Tìm blog",
        searchPlaceholder: "Tìm blog theo tiêu đề, nội dung, category, hashtag...",
        sortLabel: "Sắp xếp blog",
        sortOptions: [
            { id: "newest", label: "Mới nhất -> Cũ nhất" },
            { id: "oldest", label: "Cũ nhất -> Mới nhất" },
            { id: "popular", label: "Phổ biến / Nhiều lướt đọc nhất" },
        ],
        toc: "Mục lục",
    },
};

blogCopy.vi = {
    all: "Tất cả",
    allDates: "Tất cả thời gian",
    allHashtags: "Tất cả hashtag",
    back: "< Quay lại blog",
    breadcrumbBlog: "Bài viết",
    breadcrumbHome: "Trang chủ",
    empty: "Chưa có bài viết phù hợp với bộ lọc hiện tại.",
    filterTitle: "Chủ đề",
    hashtagLabel: "Lọc theo hashtag",
    loadMore: "Tải thêm bài viết",
    loading: "Đang tải bài viết...",
    notFound: "Không tìm thấy bài viết hoặc bài viết chưa được xuất bản.",
    openPost: "Mở bài viết",
    readPost: "Đọc bài",
    reads: (views) => `${views} lượt đọc`,
    resultCount: (count) => `${count} bài viết phù hợp`,
    searchLabel: "Tìm kiếm",
    searchPlaceholder: "Tìm theo tiêu đề, nội dung, chủ đề hoặc hashtag...",
    selectDateLabel: "Thời gian",
    sortLabel: "Sắp xếp",
    sortOptions: [
        { id: "newest", label: "Mới nhất trước" },
        { id: "oldest", label: "Cũ nhất trước" },
        { id: "popular", label: "Nhiều lượt đọc" },
    ],
    toc: "Mục lục",
};

Object.assign(blogCopy.en, {
    comments: (count) => `${count} ${count === 1 ? "comment" : "comments"}`,
    latestPosts: "Newest posts",
    sidebarTitle: "Community style feed",
    topPosts: "Most read",
    trendingTopics: "Trending topics",
    views: (count) => `${count} ${count === 1 ? "view" : "views"}`,
});

Object.assign(blogCopy.vi, {
    comments: (count) => `${count} bình luận`,
    latestPosts: "Bài viết mới nhất",
    sidebarTitle: "Feed kiểu cộng đồng",
    topPosts: "Đọc nhiều",
    trendingTopics: "Chủ đề nổi bật",
    views: (count) => `${count} lượt xem`,
});

const toMillis = (value) => {
    if (!value) {
        return 0;
    }

    const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const normalizeFilter = (value) =>
    String(value || "")
        .trim()
        .replace(/^#/, "")
        .toLowerCase();

const getPostTags = (post) =>
    Array.isArray(post.tags) ? post.tags.map((tag) => String(tag).trim()).filter(Boolean) : [];

const getPostDateValue = (post) => post.updatedAt || post.createdAt;

const getPostMonthKey = (post) => {
    const millis = toMillis(getPostDateValue(post));

    if (!millis) {
        return "";
    }

    const date = new Date(millis);
    const month = String(date.getMonth() + 1).padStart(2, "0");

    return `${date.getFullYear()}-${month}`;
};

const getInitials = (name = "") =>
    String(name)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "TN";

const formatMonthLabel = (monthKey, language) => {
    const [year, month] = monthKey.split("-").map(Number);

    if (!year || !month) {
        return monthKey;
    }

    return new Date(year, month - 1, 1).toLocaleDateString(
        language === "vi" ? "vi-VN" : "en-US",
        {
            month: "short",
            year: "numeric",
        }
    );
};

const buildBlogHaystack = (post) =>
    [
        post.title,
        post.titleVi,
        post.slug,
        stripHtml(post.excerpt),
        stripHtml(post.excerptVi),
        stripHtml(post.content),
        stripHtml(post.contentVi),
        getBlogCategories(post).join(" "),
        getPostTags(post).join(" "),
    ]
        .join(" ")
        .toLowerCase();

const splitContentBlocks = (content) =>
    content
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean);

const createHeadingId = (text, index, usedIds) => {
    const baseId = text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || `section-${index + 1}`;
    let id = baseId;
    let duplicateIndex = 2;

    while (usedIds.has(id)) {
        id = `${baseId}-${duplicateIndex}`;
        duplicateIndex += 1;
    }

    usedIds.add(id);
    return id;
};

const buildBlogDetailHtml = (content) => {
    const template = document.createElement("template");
    const usedIds = new Set();
    const sanitizedContent = sanitizeRichHtml(content);

    template.innerHTML = sanitizedContent;

    const tocItems = Array.from(template.content.querySelectorAll("h1, h2, h3, h4, h5, h6")).map((heading, index) => {
        const title = stripHtml(heading.textContent || "").trim();
        const id = heading.id || createHeadingId(title, index, usedIds);

        heading.id = id;

        return {
            id,
            level: heading.tagName.toLowerCase(),
            title,
        };
    }).filter((item) => item.title);

    return {
        html: template.innerHTML,
        tocItems,
    };
};

const PortfolioBlog = ({ activeTag, detailSlug, language, onTagChange }) => {
    const copy = blogCopy[language] || blogCopy.en;
    const navigate = useNavigate();
    const viewedPostRef = React.useRef("");
    const [activeHashtag, setActiveHashtag] = React.useState("all");
    const [blogs, setBlogs] = React.useState([]);
    const [dateFilter, setDateFilter] = React.useState("all");
    const [error, setError] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [sortMode, setSortMode] = React.useState("newest");
    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);

    React.useEffect(() => {
        const unsubscribe = firestore.collection(BLOG_COLLECTION).where("status", "==", "published").onSnapshot(
            (snapshot) => {
                const publishedBlogs = snapshot.docs
                    .map((doc) => ({ id: doc.id, ...doc.data() }));

                setBlogs(publishedBlogs);
                setError("");
                setLoading(false);
            },
            (snapshotError) => {
                setError(snapshotError.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    React.useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [activeTag, activeHashtag, dateFilter, searchQuery, sortMode]);

    const dateFilters = React.useMemo(() => {
        const monthKeys = Array.from(new Set(blogs.map((blog) => getPostMonthKey(blog)).filter(Boolean)))
            .sort((a, b) => b.localeCompare(a));

        return monthKeys.map((monthKey) => ({
            id: monthKey,
            label: formatMonthLabel(monthKey, language),
        }));
    }, [blogs, language]);

    const filteredPosts = React.useMemo(() => {
        const keyword = normalizeFilter(searchQuery);

        return blogs
            .filter((post) => {
                const categories = getBlogCategories(post);
                const tags = getPostTags(post);
                const matchesCategory =
                    activeTag === "all" ||
                    categories.some((category) => normalizeFilter(category) === activeTag);
                const matchesHashtag =
                    activeHashtag === "all" ||
                    tags.some((tag) => normalizeFilter(tag) === activeHashtag);
                const matchesDate = dateFilter === "all" || getPostMonthKey(post) === dateFilter;
                const matchesSearch = !keyword || buildBlogHaystack(post).includes(keyword);

                return matchesCategory && matchesHashtag && matchesDate && matchesSearch;
            })
            .sort((a, b) => {
                if (sortMode === "popular") {
                    return (Number(b.views) || 0) - (Number(a.views) || 0) ||
                        toMillis(getPostDateValue(b)) - toMillis(getPostDateValue(a));
                }

                if (sortMode === "oldest") {
                    return toMillis(getPostDateValue(a)) - toMillis(getPostDateValue(b));
                }

                return toMillis(getPostDateValue(b)) - toMillis(getPostDateValue(a));
            });
    }, [activeHashtag, activeTag, blogs, dateFilter, searchQuery, sortMode]);

    const normalizedDetailSlug = normalizeFilter(detailSlug);
    const isDetailMode = Boolean(normalizedDetailSlug);

    const selectedPost = React.useMemo(
        () =>
            normalizedDetailSlug
                ? blogs.find((post) => {
                    const publicSlug = normalizeFilter(post.slug || post.id);
                    const docId = normalizeFilter(post.id);

                    return publicSlug === normalizedDetailSlug || docId === normalizedDetailSlug;
                })
                : null,
        [blogs, normalizedDetailSlug]
    );

    React.useEffect(() => {
        if (!isDetailMode || !selectedPost?.id || viewedPostRef.current === selectedPost.id) {
            return;
        }

        viewedPostRef.current = selectedPost.id;

        firestore.collection(BLOG_COLLECTION).doc(selectedPost.id).update({
            lastViewedAt: firebase.firestore.FieldValue.serverTimestamp(),
            views: firebase.firestore.FieldValue.increment(1),
        }).catch((viewError) => {
            // Reading the post should not fail just because view tracking failed.
            console.warn(viewError.message);
        });
    }, [isDetailMode, selectedPost]);

    const handleOpenPost = React.useCallback((post) => {
        navigate(`/blog/${post.slug || post.id}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [navigate]);

    const visiblePosts = filteredPosts.slice(0, visibleCount);
    const canLoadMore = visibleCount < filteredPosts.length;
    const authorName = pick(aboutContent.name, language) || "Tam";
    const authorInitials = getInitials(authorName);

    const detailTitle = selectedPost ? resolveBlogText(selectedPost, "title", language) : "";
    const detailExcerpt = selectedPost ? resolveBlogText(selectedPost, "excerpt", language) : "";
    const detailExcerptHasHtml = hasHtmlContent(detailExcerpt);
    const detailContent = selectedPost
        ? resolveBlogText(selectedPost, "content", language) || detailExcerpt
        : "";
    const detailBlocks = splitContentBlocks(detailContent);
    const detailHasHtml = hasHtmlContent(detailContent);
    const detailRender = React.useMemo(
        () => (detailHasHtml ? buildBlogDetailHtml(detailContent) : { html: "", tocItems: [] }),
        [detailContent, detailHasHtml]
    );
    const detailCategories = selectedPost ? getBlogCategories(selectedPost) : [];
    const detailTags = selectedPost ? getPostTags(selectedPost) : [];

    React.useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [selectedPost?.id]);

    return (
        <div className={`portfolio-page${isDetailMode ? " is-blog-detail" : ""}`}>
            <section className={`portfolio-blog-wrap${isDetailMode ? " is-detail" : ""}`}>
                <div className="portfolio-blog-header">
                    <h1>{pick(blogContent.title, language)}</h1>
                    <p>{pick(blogContent.description, language)}</p>
                </div>

                {isDetailMode ? (
                    <>
                        {loading && (
                            <div className="portfolio-blog-state">
                                {copy.loading}
                            </div>
                        )}

                        {error && <div className="portfolio-blog-state is-error">{error}</div>}

                        {!loading && !error && !selectedPost && (
                            <div className="portfolio-blog-state">
                                {copy.notFound}
                            </div>
                        )}

                        {!loading && !error && selectedPost && (
                            <div className="portfolio-blog-detail-shell">
                                <article className="portfolio-blog-detail">
                                    <nav className="portfolio-blog-back" aria-label="Blog breadcrumb">
                                        <button type="button" onClick={() => navigate("/")}>
                                            {copy.breadcrumbHome}
                                        </button>
                                        <span aria-hidden="true">&gt;</span>
                                        <button type="button" onClick={() => navigate("/blog")}>
                                            {copy.breadcrumbBlog}
                                        </button>
                                        <span aria-hidden="true">&gt;</span>
                                        <span className="portfolio-blog-breadcrumb-current" aria-current="page">
                                            {detailTitle}
                                        </span>
                                    </nav>

                                    <div className="portfolio-blog-detail-author">
                                        <span className="portfolio-blog-author-avatar" aria-hidden="true">
                                            {authorInitials}
                                        </span>
                                        <div className="portfolio-blog-detail-author-text">
                                            <strong>{authorName}</strong>
                                            <div className="portfolio-blog-detail-meta">
                                                <span>{formatDate(getPostDateValue(selectedPost))}</span>
                                                <span>{estimateReadTime(selectedPost, language)}</span>
                                                <span>{copy.reads(Number(selectedPost.views) || 0)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <h2>{detailTitle}</h2>
                                    {detailExcerptHasHtml ? (
                                        <div
                                            className="portfolio-blog-detail-excerpt"
                                            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(detailExcerpt) }}
                                        />
                                    ) : (
                                        detailExcerpt && (
                                            <p className="portfolio-blog-detail-excerpt">{detailExcerpt}</p>
                                        )
                                    )}

                                    {selectedPost.coverImage && (
                                        <figure className="portfolio-blog-detail-cover">
                                            <img src={selectedPost.coverImage} alt={detailTitle} loading="lazy" />
                                        </figure>
                                    )}

                                    <div className="portfolio-blog-detail-tags">
                                        {detailCategories.map((category) => (
                                            <button
                                                type="button"
                                                className="portfolio-filter-tag"
                                                key={category}
                                                onClick={() => {
                                                    onTagChange(normalizeFilter(category));
                                                    navigate("/blog");
                                                }}
                                            >
                                                {category}
                                            </button>
                                        ))}
                                        {detailTags.map((tag) => (
                                            <button
                                                type="button"
                                                className="portfolio-hashtag-chip"
                                                key={tag}
                                                onClick={() => {
                                                    setActiveHashtag(normalizeFilter(tag));
                                                    navigate("/blog");
                                                }}
                                            >
                                                #{tag}
                                            </button>
                                        ))}
                                    </div>

                                    {detailHasHtml ? (
                                        <div
                                            className="portfolio-blog-detail-body"
                                            dangerouslySetInnerHTML={{ __html: detailRender.html }}
                                        />
                                    ) : (
                                        <div className="portfolio-blog-detail-body">
                                            {detailBlocks.map((block) => (
                                                <p key={block}>{block}</p>
                                            ))}
                                        </div>
                                    )}

                                    <BlogEngagement blogId={selectedPost.id} language={language} />
                                </article>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <CryptoTicker />

                        <div className="portfolio-blog-tools">
                            <label className="portfolio-blog-control portfolio-blog-search">
                                <span>{copy.searchLabel}</span>
                                <div>
                                    <FiSearch />
                                    <input
                                        value={searchQuery}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                        placeholder={copy.searchPlaceholder}
                                    />
                                </div>
                            </label>

                            <label className="portfolio-blog-control">
                                <span>{copy.sortLabel}</span>
                                <select
                                    className="portfolio-blog-select"
                                    value={sortMode}
                                    onChange={(event) => setSortMode(event.target.value)}
                                >
                                    {copy.sortOptions.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="portfolio-blog-control">
                                <span>{copy.selectDateLabel}</span>
                                <select
                                    className="portfolio-blog-select"
                                    value={dateFilter}
                                    onChange={(event) => setDateFilter(event.target.value)}
                                >
                                    <option value="all">{copy.allDates}</option>
                                    {dateFilters.map((filter) => (
                                        <option key={filter.id} value={filter.id}>
                                            {filter.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="portfolio-blog-list-shell">
                            <main className="portfolio-blog-feed">
                                <div className="portfolio-blog-feed-head">
                                    <div>
                                        <span>{copy.latestPosts}</span>
                                        <strong>{copy.resultCount(filteredPosts.length)}</strong>
                                    </div>
                                </div>

                                {loading && (
                                    <div className="portfolio-blog-state">
                                        {copy.loading}
                                    </div>
                                )}

                                {error && <div className="portfolio-blog-state is-error">{error}</div>}

                                {!loading && !error && !visiblePosts.length && (
                                    <div className="portfolio-blog-state">
                                        {copy.empty}
                                    </div>
                                )}

                                {!!visiblePosts.length && (
                                    <>
                                        <div className="portfolio-blog-grid">
                                            {visiblePosts.map((post) => {
                                                const postCategories = getBlogCategories(post);
                                                const title = resolveBlogText(post, "title", language);
                                                const excerpt = stripHtml(resolveBlogText(post, "excerpt", language));
                                                const views = Number(post.views) || 0;

                                                return (
                                                    <article
                                                        id={`blog-${post.slug || post.id}`}
                                                        key={post.id}
                                                        className="portfolio-blog-post"
                                                    >
                                                        {post.coverImage && (
                                                            <button
                                                                type="button"
                                                                className="portfolio-blog-card-cover"
                                                                onClick={() => handleOpenPost(post)}
                                                                aria-label={`${copy.openPost}: ${title}`}
                                                            >
                                                                <img src={post.coverImage} alt={title} loading="lazy" />
                                                            </button>
                                                        )}

                                                        <div className="portfolio-blog-card-body">
                                                            <div className="portfolio-blog-card-meta">
                                                                <span className="portfolio-blog-author-avatar" aria-hidden="true">
                                                                    {authorInitials}
                                                                </span>
                                                                <div className="portfolio-blog-card-meta-text">
                                                                    <strong>{authorName}</strong>
                                                                    <span>
                                                                        {formatDate(getPostDateValue(post))}
                                                                        <i aria-hidden="true">·</i>
                                                                        {estimateReadTime(post, language)}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <h3>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleOpenPost(post)}
                                                                >
                                                                    {title}
                                                                </button>
                                                            </h3>
                                                            {excerpt && <p>{excerpt}</p>}

                                                            {!!postCategories.length && (
                                                                <div className="portfolio-blog-category-list">
                                                                    {postCategories.map((category) => (
                                                                        <button
                                                                            type="button"
                                                                            className="portfolio-blog-tag"
                                                                            key={category}
                                                                            onClick={() => onTagChange(normalizeFilter(category))}
                                                                        >
                                                                            {category}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            <div className="portfolio-blog-card-foot" aria-label="Post stats">
                                                                <span>
                                                                    <FiEye />
                                                                    {copy.views(views)}
                                                                </span>
                                                                <span>
                                                                    <FiMessageCircle />
                                                                    {copy.comments(Number(post.commentCount) || 0)}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="portfolio-blog-card-open"
                                                                    onClick={() => handleOpenPost(post)}
                                                                    aria-label={`${copy.openPost}: ${title}`}
                                                                >
                                                                    {copy.readPost}
                                                                    <FiArrowRight />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                        </div>

                                        {canLoadMore && (
                                            <div className="portfolio-load-more-wrap">
                                                <button
                                                    type="button"
                                                    className="portfolio-btn portfolio-btn-secondary portfolio-load-more"
                                                    onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                                                >
                                                    {copy.loadMore}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </main>

                            {/* <aside className="portfolio-blog-sidebar" aria-label={copy.sidebarTitle}>
                                {hashtagFilters.length > 1 && (
                                    <section>
                                        <div className="portfolio-blog-sidebar-title">
                                            <FiBookmark />
                                            <span>{copy.hashtagLabel}</span>
                                        </div>
                                        <div className="portfolio-hashtag-row" aria-label={copy.hashtagLabel}>
                                            {hashtagFilters.map((filter) => (
                                                <button
                                                    type="button"
                                                    key={filter.id}
                                                    className={`portfolio-hashtag-chip${activeHashtag === filter.id ? " is-active" : ""}`}
                                                    onClick={() => setActiveHashtag(filter.id)}
                                                >
                                                    {filter.id === "all" ? filter.label : `#${filter.label}`}
                                                </button>
                                            ))}
                                        </div>
                                    </section>
                                )}
                            </aside> */}
                        </div>
                    </>
                )}
            </section>

            <PageFooter language={language}>
                <span>{pick(aboutContent.name, language)}</span> - {pick(blogContent.footer, language)}
            </PageFooter>
        </div>
    );
};

export default PortfolioBlog;
